import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, FormSection, Input, SectionCard, Skeleton, Switch, toast } from '@/components/ui';
import {
  useHomeHeroCaptionMutation,
  useHomeHeroCaptionQuery,
} from '../../hooks/useHomeHeroCaptionQuery';
import type { HomeHeroCaptionRequest } from '../../types/cms';

// QA-102: SF accent 기본색(highlightColor 미지정 시 미리보기 폴백). SF 토큰과 동일한 값.
const DEFAULT_ACCENT = '#EE2737';

const EMPTY_FORM: HomeHeroCaptionRequest = {
  textBefore: '',
  highlightWord: '',
  textAfter: '',
  highlightColor: null,
  isActive: true,
};

/**
 * QA-102 — 메인 히어로 헤드라인 캡션 편집 패널.
 * 'textBefore [highlightWord] textAfter' 3분할 입력 + 강조 색상 선택 + 실시간 미리보기.
 * BE(catalog) GET 으로 prefill, PUT(싱글톤 upsert, Idempotency-Key)으로 저장한다.
 */
export const HeroCaptionPanel = () => {
  const { t } = useTranslation('common');
  const query = useHomeHeroCaptionQuery();
  const mutation = useHomeHeroCaptionMutation();

  const [form, setForm] = useState<HomeHeroCaptionRequest>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) {
      setForm({
        textBefore: query.data.textBefore,
        highlightWord: query.data.highlightWord,
        textAfter: query.data.textAfter,
        highlightColor: query.data.highlightColor,
        isActive: query.data.isActive,
      });
    }
  }, [query.data]);

  const set = <K extends keyof HomeHeroCaptionRequest>(key: K, val: HomeHeroCaptionRequest[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setError(null);
    try {
      await mutation.mutateAsync(form);
      toast.success(t('cms.heroCaption.saved', { defaultValue: '히어로 헤드라인이 저장되었습니다.' }));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('cms.heroCaption.saveFailed', { defaultValue: '저장에 실패했습니다.' }));
    }
  };

  if (query.isPending) {
    return (
      <div role="status" aria-live="polite" className="space-y-2">
        <Skeleton className="h-[44px] w-full" />
        <Skeleton className="h-[44px] w-full" />
      </div>
    );
  }

  const accent = form.highlightColor && form.highlightColor.trim() ? form.highlightColor : DEFAULT_ACCENT;

  return (
    <div className="flex flex-col gap-5">
      <SectionCard
        title={t('cms.heroCaption.title', { defaultValue: '히어로 헤드라인' })}
        description={t('cms.heroCaption.subtitle', {
          defaultValue: '메인 상단 헤드라인을 앞·강조·뒤 문구로 나누어 입력하세요. 강조 문구는 선택한 색으로 표시됩니다.',
        })}
      >
        <div className="flex flex-col gap-3">
          <FormSection label={t('cms.heroCaption.fields.textBefore', { defaultValue: '앞 문구' })} density="compact">
            <Input
              type="text"
              value={form.textBefore}
              onChange={(e) => set('textBefore', e.target.value)}
              maxLength={100}
              placeholder={t('cms.heroCaption.fields.textBeforePlaceholder', { defaultValue: '예: 지금 가장' })}
            />
          </FormSection>
          <FormSection
            label={t('cms.heroCaption.fields.highlightWord', { defaultValue: '강조 문구' })}
            density="compact"
          >
            <Input
              type="text"
              value={form.highlightWord}
              onChange={(e) => set('highlightWord', e.target.value)}
              maxLength={100}
              placeholder={t('cms.heroCaption.fields.highlightWordPlaceholder', { defaultValue: '예: HOT한' })}
            />
          </FormSection>
          <FormSection label={t('cms.heroCaption.fields.textAfter', { defaultValue: '뒤 문구' })} density="compact">
            <Input
              type="text"
              value={form.textAfter}
              onChange={(e) => set('textAfter', e.target.value)}
              maxLength={100}
              placeholder={t('cms.heroCaption.fields.textAfterPlaceholder', { defaultValue: '예: 상품을 만나보세요' })}
            />
          </FormSection>
          <FormSection
            label={t('cms.heroCaption.fields.highlightColor', { defaultValue: '강조 색상' })}
            density="compact"
            hint={t('cms.heroCaption.fields.highlightColorHint', {
              defaultValue: '비우면 기본 강조색이 적용됩니다.',
            })}
          >
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={accent}
                onChange={(e) => set('highlightColor', e.target.value)}
                aria-label={t('cms.heroCaption.fields.highlightColor', { defaultValue: '강조 색상' })}
                className="h-9 w-12 cursor-pointer rounded border"
                style={{ borderColor: 'var(--divider-strong)' }}
              />
              <Input
                type="text"
                value={form.highlightColor ?? ''}
                onChange={(e) => set('highlightColor', e.target.value || null)}
                maxLength={9}
                placeholder={DEFAULT_ACCENT}
                className="max-w-[140px]"
              />
            </div>
          </FormSection>
          <FormSection label={t('cms.heroCaption.fields.isActive', { defaultValue: '활성 여부' })} density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label={t('cms.heroCaption.fields.isActive', { defaultValue: '활성 여부' })}
            />
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard
        title={t('cms.heroCaption.previewTitle', { defaultValue: '미리보기' })}
        description={t('cms.heroCaption.previewSubtitle', {
          defaultValue: '스토어프론트 상단에 아래와 같이 노출됩니다.',
        })}
      >
        <p
          className="text-2xl font-bold leading-snug"
          style={{ color: 'var(--content-strong)' }}
          data-testid="hero-caption-preview"
        >
          {form.textBefore}
          {form.textBefore && form.highlightWord ? ' ' : ''}
          <span style={{ color: accent }}>{form.highlightWord}</span>
          {form.highlightWord && form.textAfter ? ' ' : ''}
          {form.textAfter}
        </p>
      </SectionCard>

      {error && (
        <div
          role="alert"
          className="rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button size="sm" onClick={() => void handleSubmit()} disabled={mutation.isPending}>
          {mutation.isPending ? t('cms.saving', { defaultValue: '저장 중…' }) : t('cms.save')}
        </Button>
      </div>
    </div>
  );
};
